/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import classNames from 'classnames';

import { USER_ROLES } from '@pwc/pwc-deals-web-common';
import {
  getUserRole,
  setActualUserRole,
  setUserRole,
} from 'common/redux/security/loggedInUser/loggedInUserSlice';

import { Link, useHistory } from 'react-router-dom';
import { Icon } from '@pwc/pwc-deals-ui';
import { getUserSettingsSelector } from 'common/redux/userSettings/userSettingsSlice';
import { isProjectListingEnabled } from 'common/helpers/featureFlags';
import UserSnippet from '../../common/components/UserSnippet';
import DealDetails from '../../common/components/DealDetails';
import Separator from '../../common/components/Separator';
import { getUserForList } from '../project/common/ProjectModel';
import {
  getProjectStatus,
  useProjectsStatuses,
} from '../../common/hooks/useProjectsStatuses';
import NewAndUpdated from '../project/common/NewAndUpdated.tsx';
import { getUserViewStatusCountsForProject } from '../../common/services/projectService';
import userTokenService, {
  getPlaintextToken,
} from '../../common/services/security/userTokenService';
import {
  isProjectActive,
  isProjectReadOnly,
} from '../../common/helpers/projectStatusesUtils';
import ProjectStatus from './ProjectStatus';
import Accordion from '../../common/components/Accordion/AccordionOnlyMobile';
import featureFlags from '../../common/helpers/featureFlags';
import { projectOverviewPath } from '../../common/services/urlService';
import {
  addToFavourite,
  deleteFromFavourite,
  getProjectFavInProgress,
} from './pagedProjectsSlice';

const ProjectListRow = ({ project, isFavourite = false, isSearch = false }) => {
  const { t } = useTranslation();
  const [counts, setCounts] = useState({ newCount: 0, updatedCount: 0 });
  const [projectToken, setProjectToken] = useState(null);
  const projectsStates = useProjectsStatuses({ isFavourite, isSearch });
  const projectState = getProjectStatus(projectsStates, project.id);
  const isMounted = useRef(true);
  const history = useHistory();

  const userRole = useSelector(getUserRole);
  const decodedToken = getPlaintextToken(project.id, userRole);
  const isPwcUser =
    userRole === USER_ROLES.PWC_USER &&
    decodedToken?.role?.toLowerCase() !== 'third party member';
  const dispatch = useDispatch();
  const userSettings = useSelector(getUserSettingsSelector);
  const isDarkMode = userSettings.settings.darkMode;
  const projectFavInProgress = useSelector(getProjectFavInProgress);

  let iconName = 'star-light';

  if (isDarkMode) {
    iconName = 'star-dark';
  }
  if (project.isUserFavorite) {
    iconName += '-selected';
  }

  useEffect(() => {
    const token = userTokenService.getProjectToken(project.id);
    setProjectToken(token);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    isMounted.current = true;
    const getContentUpdates = async () => {
      const projectStatusCompleted = 3;
      if (
        projectToken &&
        project.status === projectStatusCompleted &&
        isMounted.current &&
        !featureFlags.isDisabledCountApi
      )
        setCounts(await getUserViewStatusCountsForProject(project.id));
    };

    getContentUpdates();
    const timerId = setInterval(getContentUpdates, 60000);

    return () => {
      isMounted.current = false;
      clearInterval(timerId);
    };
  }, [projectToken, project.id, project.status]);

  const engagementLead = getUserForList(project, 'engagementLead');
  const engagementManager = getUserForList(project, 'engagementManager');
  const isProjectReadable =
    isProjectReadOnly(project.status) || isProjectActive(project.status);

  const [projectStatusClass, setProjectStatusClass] = useState('');

  useEffect(() => {
    isMounted.current = true;

    if (isMounted.current) {
      switch (project.status) {
        case 1:
          setProjectStatusClass(' new');
          break;
        case 2:
          setProjectStatusClass(' in-progress');
          break;
        case 3:
          setProjectStatusClass(' completed');
          break;
        case 4:
          setProjectStatusClass(' migration-completed');
          break;
        case 5:
          setProjectStatusClass(' migration-failed');
          break;
        case 7:
          setProjectStatusClass(' sync-failed');
          break;
        case 10:
          setProjectStatusClass(' failed');
          break;
        case 11:
          setProjectStatusClass(' on-hold');
          break;
        default:
          setProjectStatusClass('');
      }
    }

    return () => {
      isMounted.current = false;
    };
  }, [setProjectStatusClass, project]);

  const projectUrl = projectOverviewPath(project.id, isPwcUser);

  const handleFavourite = (isUserFavorite, projectId) => {
    if (isUserFavorite) {
      dispatch(deleteFromFavourite({ projectId }));
    } else {
      dispatch(addToFavourite({ projectId }));
    }
  };

  const handleClickProjectTitle = (e) => {
    e.preventDefault();
    if (decodedToken?.role?.toLowerCase() === 'third party member') {
      dispatch(setUserRole(USER_ROLES.THIRD_PARTY));
      dispatch(setActualUserRole(USER_ROLES.THIRD_PARTY));
    } else if (decodedToken?.role?.toLowerCase().includes('client')) {
      dispatch(setUserRole(USER_ROLES.CLIENT));
      dispatch(setActualUserRole(USER_ROLES.CLIENT));
    } else {
      dispatch(setUserRole(USER_ROLES.PWC_USER));
      dispatch(setActualUserRole(USER_ROLES.PWC_USER));
    }
    history.push(projectUrl);
  };

  return (
    <div
      className={classNames('project-panel', {
        'project-panel__readonly': isProjectReadOnly(projectState),
      })}
    >
      <div className="project-panel-row">
        {isProjectListingEnabled ? (
          <div className="project-title-description-container">
            <button
              type="button"
              className="star-btn"
              onClick={() => {
                handleFavourite(project.isUserFavorite, project.id);
              }}
              disabled={project.id === projectFavInProgress}
            >
              <Icon name={iconName} width={24} className="outline icon-grey" />
            </button>
            <span
              className={`project-title${projectStatusClass} project-title-list`}
            >
              <button
                type="button"
                data-testid={`project-${project.id}-link`}
                className="projectListRowBtn"
                onClick={handleClickProjectTitle}
              >
                {project.name}
              </button>
              <div className="pills-container">
                <ProjectStatus
                  project={project}
                  isFavourite={isFavourite}
                  isSearch={isSearch}
                />
              </div>
            </span>
            <Link
              to={projectUrl}
              className="project-panel-link-arrow"
              data-testid={`project-${project.id}-link-arrow`}
            >
              <Icon
                name="arrow-right"
                width={32}
                className="project-panel-arrow outline icon-orange"
              />
            </Link>
          </div>
        ) : (
          <div className="project-title-description-container">
            <span className={`project-title${projectStatusClass}`}>
              <button
                type="button"
                data-testid={`project-${project.id}-link`}
                className="projectListRowBtn"
                onClick={handleClickProjectTitle}
              >
                {project.name}
              </button>
              <div className="pills-container">
                <ProjectStatus
                  project={project}
                  isFavourite={isFavourite}
                  isSearch={isSearch}
                />
              </div>
            </span>
            <Link
              to={projectUrl}
              className="project-panel-link-arrow"
              data-testid={`project-${project.id}-link-arrow`}
            >
              <Icon
                name="arrow-right"
                width={32}
                className="project-panel-arrow outline icon-orange"
              />
            </Link>
          </div>
        )}
      </div>
      <div className="project-description-container">
        {!featureFlags.isDisabledCountApi && (
          <div className="project-content">
            {projectToken && (
              <NewAndUpdated
                projectId={project.id}
                newContent={counts.newCount}
                updatedContent={counts.updatedCount}
                shouldRetrieveNotificationCount={isProjectReadable}
              />
            )}
          </div>
        )}
      </div>
      {projectToken && isPwcUser && (
        <>
          <Separator />
          <Accordion title={t('info')}>
            <div className="details-container">
              <div className="user-details-container">
                <div>
                  <UserSnippet
                    dataTestId={`project-${project.id}-engagement-lead`}
                    firstName={engagementLead.firstName}
                    lastName={engagementLead.lastName}
                    position="Engagement Lead"
                  />
                </div>
                <div>
                  <UserSnippet
                    dataTestId={`project-${project.id}-engagement-manager`}
                    firstName={engagementManager.firstName}
                    lastName={engagementManager.lastName}
                    position="Engagement Manager"
                  />
                </div>
              </div>
              <div className="deal-details-container">
                <div className="detail-section">
                  <DealDetails
                    dataTestId={`project-${project.id}-territory-details`}
                    title={t('leadTerritory')}
                    content={project.territory}
                  />
                </div>
                <div className="detail-section">
                  <DealDetails
                    dataTestId={`project-${project.id}-client-details`}
                    title={t('client')}
                    content={project.clientName}
                  />
                </div>
              </div>
            </div>
          </Accordion>
        </>
      )}
    </div>
  );
};

export default ProjectListRow;
